/**
 * What chrome each installed app gets: which nav rows, its start tab, and how
 * it brands itself.
 *
 * A derivation over the destinations catalog and `APP_NAV` rather than a
 * parallel nav tree: the rail inside an area app is that app's membership.
 */
import { DESTINATIONS } from '@shared/navigation/destinations';

import { APP_NAV, startTabFor } from './appNav';

import type { AppSlug } from './installedApp';

export interface AppChrome {
  /**
   * What the app calls itself to its own user: "Inbox", "Tasks", "Food".
   *
   * Used wherever the app is naming *itself* — the splash and the login
   * greeting. Inside Conway Tasks the "Conway" is redundant: the tile is right
   * there, and the home-screen label under the icon already reads just "Tasks".
   * Must match each manifest's `short_name`, which is what draws that label —
   * pinned by a test, because a drift here shows as two different names for one
   * app on one screen.
   */
  shortName: string;
  /** Home-screen icon, used on the login screen. */
  icon: string;
  /** The same art at 512px, for the splash — where it renders far larger than 192px. */
  iconLarge: string;
  /** Whether the Go box offers Ask Conway — root and the Chat app. */
  showsAsk: boolean;
  /** Own destinations. Drives the minimal shell: utility rows and the pinned rail. */
  destinationCount: number;
  /** The catalog destination the app opens-or-focuses on launch; null for root Conway. */
  startTab: string | null;
}

/** Slug → the name and whether that app offers Ask Conway. Icons are derived from the slug. */
const APP_CHROME: Record<AppSlug, { shortName: string; showsAsk: boolean }> = {
  attention: { shortName: 'Inbox', showsAsk: false },
  tasks: { shortName: 'Tasks', showsAsk: false },
  food: { shortName: 'Food', showsAsk: false },
  chat: { shortName: 'Chat', showsAsk: true },
  study: { shortName: 'Study', showsAsk: false },
};

/**
 * The Go box's Ask row is a place to go; counted like a destination so the
 * Chat app keeps its chrome.
 */
const ASK_DESTINATIONS = 1;

/** How many places an app's rail holds; the root app counts every catalog destination but All areas, the launcher. */
const destinationsWithin = (slug: AppSlug | null): number =>
  slug === null ? DESTINATIONS.filter(d => d.id !== 'areas').length : APP_NAV[slug].length;

/** Name, icon and row config for the running app. */
export function chromeFor(slug: AppSlug | null): AppChrome {
  if (slug === null) {
    return {
      // The root app has no single function to name — it is all of them.
      shortName: 'Conway',
      icon: '/icons/conway-192.png',
      iconLarge: '/icons/conway-512.png',
      showsAsk: true,
      destinationCount: destinationsWithin(null),
      startTab: null,
    };
  }

  const config = APP_CHROME[slug];
  return {
    shortName: config.shortName,
    icon: `/icons/app-${slug}-192x192.png`,
    iconLarge: `/icons/app-${slug}-512x512.png`,
    showsAsk: config.showsAsk,
    destinationCount: destinationsWithin(slug) + (config.showsAsk ? ASK_DESTINATIONS : 0),
    startTab: startTabFor(slug),
  };
}

/**
 * Apps that get the minimal shell: no utility rows, and a sidebar pinned to its
 * icon rail.
 *
 * The bar is the same one the nav list used to be gated on — fewer than two
 * destinations of its own — because an app with one route has nothing for a
 * Search row to reach past its single nav row, and nothing that a 240px column
 * shows and a 64px rail does not.
 *
 * It no longer hides the nav row itself: that row is the app's only marker of
 * where it is and its only way back from the profile pages. See `StarredRows`.
 *
 * In practice this is Tasks. Study carries Notes, Journal and Research, Food carries
 * Meals, so both clear the bar.
 */
function hasMinimalChrome(slug: AppSlug | null): boolean {
  return chromeFor(slug).destinationCount < 2;
}

/**
 * Whether the sidebar's utility rows — Home (browser tab only) and Search —
 * render.
 *
 * In Tasks the whole sidebar above the profile footer was one Home icon and one
 * Search icon, standing over the empty space where a nav list would be.
 *
 * No installed app carries a Home/escape row at all: the way back to Conway is
 * the profile menu's "Open Conway", a document navigation, because manifest
 * scope is evaluated only on document-level navigations and a router
 * `navigate()` never leaves an installed app.
 */
export function showsUtilityRows(slug: AppSlug | null): boolean {
  return !hasMinimalChrome(slug);
}

/**
 * Whether the sidebar is pinned to its 64px icon rail, with no expand control.
 *
 * Once the utility rows are gone the column holds one nav icon and the profile
 * avatar, and widening it to 240px reveals only that icon's label — a control
 * whose effect is one word and a lot of empty space.
 *
 * Overrides the stored preference at render time and never writes to it: the
 * same account in a browser tab keeps whatever width the user chose there.
 */
export function pinsSidebarCollapsed(slug: AppSlug | null): boolean {
  return hasMinimalChrome(slug);
}
